import { create } from 'zustand';
import ServiceMensagensEcac from '@/services/ServiceMensagensEcac';
import { notify } from '@/stores/useToast';
import { mensagemErro } from '@/utils/apiError';

type Any = any;
export type Aba = 'todas' | 'nao-lidas' | 'lidas';

// aba -> filtro.lida do /search (todas = sem filtro)
const LIDA_DA_ABA: Record<Aba, boolean | undefined> = {
  todas: undefined,
  'nao-lidas': false,
  lidas: true,
};

// CON-5188: cada coluna ordenável da tabela mapeia para o alias que o backend aceita
// (mensagens-ecac/search?sort=<alias>,<asc|desc> no body). Só as colunas com alias
// entram como sortable; `recebida` é a data de envio (enviada_em).
const SORT_ALIASES: Record<string, string> = {
  descricaoOrigem: 'descricao_origem',
  assunto: 'assunto',
  recebida: 'enviada_em',
  validade: 'validade',
};

const fmtDataHora = (v?: string) =>
  v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-';
const fmtData = (v?: string) =>
  v ? new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : '-';

let listaReq = 0;
let dashboardReq = 0;

interface Dashboard {
  total: number;
  naoLidas: number;
  altaRelevancia: number;
}

interface State {
  cnpjCliente: string;
  aba: Aba;
  lista: Any[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  dashboard: Dashboard | null;
  carregando: boolean;
  sincronizando: boolean;

  selecionarEmpregador: (cnpj: string) => Promise<void>;
  carregar: () => Promise<void>;
  carregarDashboard: () => Promise<void>;
  mudarAba: (a: Aba) => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setSize: (size: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  sincronizar: () => Promise<void>;
}

// item da API -> linha da tabela.
const normalizar = (m: Any) => ({
  id: m.id,
  descricaoOrigem: m.descricaoOrigem ?? '',
  assunto: m.assunto ?? '',
  recebida: fmtDataHora(m.enviadaEm),
  validade: fmtData(m.validade),
  lida: !!m.lida,
  altaRelevancia: !!m.altaRelevancia,
  temAnexos: !!m.temAnexos,
});

const ESTADO_INICIAL = {
  cnpjCliente: '',
  aba: 'todas' as Aba,
  lista: [] as Any[],
  total: 0,
  page: 0,
  size: 50,
  sortBy: '',
  sortOrder: 'asc' as 'asc' | 'desc',
  dashboard: null,
  carregando: false,
  sincronizando: false,
};

export const useMensagensEcacStore = create<State>((set, get) => ({
  ...ESTADO_INICIAL,

  async selecionarEmpregador(cnpj) {
    set({ cnpjCliente: cnpj, page: 0, lista: [], total: 0, dashboard: null });
    await Promise.all([get().carregar(), get().carregarDashboard()]);
  },

  async carregar() {
    const { cnpjCliente, aba, page, size, sortBy, sortOrder } = get();
    const req = ++listaReq;
    if (!cnpjCliente) {
      set({ lista: [], total: 0, carregando: false });
      return;
    }
    set({ carregando: true });
    try {
      const alias = SORT_ALIASES[sortBy];
      const sort = alias ? `${alias},${sortOrder}` : undefined;
      const { data } = await ServiceMensagensEcac.search({
        cnpj: cnpjCliente,
        filtro: { lida: LIDA_DA_ABA[aba] },
        page,
        size,
        ...(sort ? { sort } : {}),
      });
      if (req === listaReq && get().cnpjCliente === cnpjCliente) {
        set({ lista: (data?.items ?? []).map(normalizar), total: data?.total ?? 0 });
      }
    } catch {
      if (req === listaReq && get().cnpjCliente === cnpjCliente) {
        set({ lista: [], total: 0 });
        notify.error('Erro ao carregar as mensagens.');
      }
    } finally {
      if (req === listaReq) set({ carregando: false });
    }
  },

  async carregarDashboard() {
    const { cnpjCliente } = get();
    const req = ++dashboardReq;
    if (!cnpjCliente) {
      set({ dashboard: null });
      return;
    }
    try {
      const { data } = await ServiceMensagensEcac.dashboard(cnpjCliente);
      if (req === dashboardReq && get().cnpjCliente === cnpjCliente) {
        set({ dashboard: data ?? null });
      }
    } catch {
      if (req === dashboardReq) set({ dashboard: null });
    }
  },

  async mudarAba(a) {
    set({ aba: a, page: 0 });
    await get().carregar();
  },

  async setPage(page) {
    set({ page });
    await get().carregar();
  },

  async setSize(size) {
    set({ size, page: 0 });
    await get().carregar();
  },

  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().carregar();
  },

  async sincronizar() {
    const { cnpjCliente } = get();
    if (!cnpjCliente) {
      notify.error('Selecione um empregador.');
      return;
    }
    set({ sincronizando: true });
    try {
      await ServiceMensagensEcac.sincronizar(cnpjCliente);
      notify.success('Caixa postal sincronizada.');
      await Promise.all([get().carregar(), get().carregarDashboard()]);
    } catch (err) {
      notify.error(mensagemErro(err, 'Erro ao sincronizar a caixa postal.'));
    } finally {
      set({ sincronizando: false });
    }
  },
}));
